import { Badge, Group, Modal, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMemo, useState } from 'react';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { formatCurrency, formatDate, formatDateTime } from './components/billingFormatters';
import { MOCK_ACCOUNTS, type AccountLedgerEntry, type AccountRow } from './mock/mockAccounts';

const ENTRY_TYPE_LABELS: Record<AccountLedgerEntry['type'], string> = {
  charge: 'Charge',
  payment: 'Payment',
  adjustment: 'Adjustment',
  'write-off': 'Write-off',
  'payer-payment': 'Payer Payment',
};

export function AccountsPage(): JSX.Element {
  const [opened, { open, close }] = useDisclosure(false);
  const [selected, setSelected] = useState<AccountRow | null>(null);

  const columns = useMemo(
    (): UnifyTableColumn<AccountRow>[] => [
      {
        key: 'clientName',
        label: 'Client',
        render: (row) => <Text size="sm" fw={600}>{row.clientName}</Text>,
        getValue: (row) => row.clientName,
      },
      {
        key: 'guarantor',
        label: 'Guarantor',
        getValue: (row) => row.guarantor,
      },
      {
        key: 'openBalance',
        label: 'Open Balance',
        align: 'right',
        render: (row) => <Text size="sm" fw={600}>{formatCurrency(row.openBalance)}</Text>,
        getValue: (row) => String(Math.round(row.openBalance * 100)).padStart(8, '0'),
      },
      {
        key: 'lastPaymentAt',
        label: 'Last Payment',
        render: (row) => (
          <Text size="sm" c="dimmed">
            {row.lastPaymentAt ? formatDateTime(row.lastPaymentAt) : '—'}
          </Text>
        ),
        getValue: (row) => row.lastPaymentAt,
      },
    ],
    [],
  );

  const ledgerColumns = useMemo(
    (): UnifyTableColumn<AccountLedgerEntry>[] => [
      {
        key: 'date',
        label: 'Date',
        render: (row) => <Text size="sm">{formatDate(row.date)}</Text>,
        getValue: (row) => row.date,
      },
      {
        key: 'type',
        label: 'Type',
        render: (row) => (
          <Badge variant="light" size="sm">
            {ENTRY_TYPE_LABELS[row.type]}
          </Badge>
        ),
        getValue: (row) => row.type,
      },
      {
        key: 'description',
        label: 'Description',
        getValue: (row) => row.description,
      },
      {
        key: 'debit',
        label: 'Debit',
        align: 'right',
        render: (row) => <Text size="sm">{row.debit ? formatCurrency(row.debit) : '—'}</Text>,
        getValue: (row) => String(row.debit),
      },
      {
        key: 'credit',
        label: 'Credit',
        align: 'right',
        render: (row) => <Text size="sm">{row.credit ? formatCurrency(row.credit) : '—'}</Text>,
        getValue: (row) => String(row.credit),
      },
      {
        key: 'balance',
        label: 'Balance',
        align: 'right',
        render: (row) => <Text size="sm">{formatCurrency(row.balance)}</Text>,
        getValue: (row) => String(row.balance),
      },
    ],
    [],
  );

  return (
    <>
      <UnifyTable
        title="Accounts"
        subtitle="Patient ledgers"
        columns={columns}
        data={MOCK_ACCOUNTS}
        getRowKey={(row) => row.id}
        searchable
        searchPlaceholder="Search by client or guarantor…"
        sortable
        pagination
        defaultRowsPerPage={10}
        onRowClick={(row) => {
          setSelected(row);
          open();
        }}
      />

      <Modal
        opened={opened}
        onClose={close}
        title={selected ? `${selected.clientName} — Ledger` : 'Ledger'}
        size="xl"
        centered
      >
        {selected && (
          <Stack gap="md">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Guarantor: {selected.guarantor}
              </Text>
              <Text size="sm" fw={700}>
                Open balance: {formatCurrency(selected.openBalance)}
              </Text>
            </Group>
            <UnifyTable
              withPaper={false}
              columns={ledgerColumns}
              data={selected.entries}
              getRowKey={(row) => `${row.date}-${row.type}-${row.description}`}
              searchable={false}
              sortable={false}
              pagination={false}
            />
          </Stack>
        )}
      </Modal>
    </>
  );
}
